'use client'

import React, { createContext, useContext, useState, useCallback } from 'react'
import { type TopNavigationProps } from '.'

type UpdateTopNavigationOptions = {
  /** Replace all top navigation props instead of merging with previous state. */
  replace?: boolean
}

interface TopNavigationContextValue {
  updateTopNavigation: (
    props: Partial<TopNavigationProps> | null,
    options?: UpdateTopNavigationOptions,
  ) => void
  topNavigationProps: TopNavigationProps | undefined
}

const TopNavigationContext = createContext<
  TopNavigationContextValue | undefined
>(undefined)

// Create a no-op version of the context value
const noopContextValue: TopNavigationContextValue = {
  updateTopNavigation: () => {},
  topNavigationProps: undefined,
}

export function useTopNavigation(defaultProps?: Partial<TopNavigationProps>) {
  const context = useContext(TopNavigationContext)

  // If there's no context, return the no-op version
  if (!context) {
    return noopContextValue
  }

  React.useEffect(() => {
    if (defaultProps) {
      context.updateTopNavigation(defaultProps)
    }
  }, []) // Empty dependency array since we only want this to run once on mount

  return context
}

export function TopNavigationProvider({
  children,
  defaultProps,
}: {
  children: React.ReactNode
  defaultProps?: Partial<TopNavigationProps>
}) {
  const [topNavigationProps, setTopNavigationProps] = useState<
    TopNavigationProps | undefined
  >(defaultProps as TopNavigationProps | undefined)

  const updateTopNavigation = useCallback(
    (
      props: Partial<TopNavigationProps> | null,
      options?: UpdateTopNavigationOptions,
    ) => {
      if (props === null) {
        setTopNavigationProps(undefined)
      } else if (options?.replace) {
        setTopNavigationProps(props as TopNavigationProps)
      } else {
        setTopNavigationProps((prev: TopNavigationProps | undefined) => ({
          ...prev,
          ...props,
        }))
      }
    },
    [],
  )

  return (
    <TopNavigationContext.Provider
      value={{ topNavigationProps, updateTopNavigation }}
    >
      {children}
    </TopNavigationContext.Provider>
  )
}
